import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';

import {temporaryHtmlToString} from 'nutshell-core/list-items/temporary-html-to-string';
import type {FormattedValue} from 'nutshell-core/types';
import ReactDOMServer from 'react-dom/server';

import {colors} from '../colors/colors';
import {LeadFormattedValue} from '../lead-card/lead-formatted-value';
import {RelatedEntity} from '../lead-card/related-entity';
import {LinkedLeadEntity} from '../linked-entity/linked-lead-entity';
import {LoadingText} from '../loading-placeholder/loading-text';

import {LeadAvatar} from './lead-avatar';
import styles from './lead-two-line.module.css';

type Props = {
    isLinkable?: boolean;
    newTab?: boolean;
    disableRouting?: boolean;
    isLoading?: boolean;
    leadName: string;
    leadNumber: number | null | undefined;
    avatarUrl: string;
    priority?: string;
    htmlUrl: string;
    leadStatus: string;
    relatedPerson?:
        | {
              id: string;
              htmlUrl: string;
              name: string;
          }
        | null
        | undefined;
    relatedCompany?:
        | {
              id: string;
              htmlUrl: string;
              name: string;
          }
        | null
        | undefined;
    // We don't always require these two properties, for situations
    // like the entity list which doesn't want additional metadata
    // on the right side of the two-line information
    overdueDuration?: string | null | undefined;
    formattedValue?: FormattedValue;
    withLessPadding?: boolean;
    rightContent?: React.ReactNode | null | undefined;
    // Restricts height
    isSmall?: boolean;
    // In certain cases the line height causes the entity to get cut off, but in
    // other cases it is necessary for formatting
    noAssociatedEntityLineHeight?: boolean;
    largerLeadName?: boolean;
    noCenterAlignTopRow?: boolean;
};

export class LeadTwoLine extends React.PureComponent<Props> {
    componentDidMount() {
        ReactTooltip.rebuild();
    }

    render() {
        if (this.props.isLoading) {
            return this.renderLoadingPlaceholder();
        }

        // If both related entity fields aren't even defined, then don't try to show second row
        const hideRelatedEntity =
            typeof this.props.relatedCompany === 'undefined' &&
            typeof this.props.relatedPerson === 'undefined';

        const styleNames = classnames({
            [styles['lead-two-line']]: !this.props.leadNumber,
            [styles['lead-two-line--with-number']]: this.props.leadNumber,
            [styles['less-padding']]: this.props.withLessPadding,
            [styles['is-small']]: this.props.isSmall,
        });

        const sanitizedLeadName = temporaryHtmlToString(this.props.leadName) || this.props.leadName;

        // The bottom row might not have content, we don't want to render it in that case
        let bottomRowContent;
        if (!hideRelatedEntity) {
            // possible related entity, could be null
            let relatedEntity = (
                <RelatedEntity
                    isLinkable={Boolean(this.props.isLinkable)}
                    relatedPerson={this.props.relatedPerson}
                    relatedCompany={this.props.relatedCompany}
                    leadName={sanitizedLeadName}
                />
            );

            // converts jsx to a string, is empty if nothing is rendered by that component
            if (!ReactDOMServer.renderToStaticMarkup(relatedEntity)) {
                // @ts-expect-error
                relatedEntity = null;
            }

            // could be overdue duration
            const overdueDuration = this.props.overdueDuration ? (
                <div
                    data-tip={`This lead is overdue by ${this.props.overdueDuration}`}
                    className={styles['overdue-duration']}
                >
                    {this.props.overdueDuration}
                </div>
            ) : undefined;

            if (relatedEntity || overdueDuration) {
                const associatedStyleName = classnames(styles.associated, {
                    [styles['associated-no-line-height']]: this.props.noAssociatedEntityLineHeight,
                });

                bottomRowContent = (
                    <>
                        {relatedEntity && (
                            <div className={associatedStyleName}>{relatedEntity}</div>
                        )}
                        {overdueDuration}
                    </>
                );
            }
        }

        const leadInfoStyleName = classnames({
            [styles['lead-info']]: true,
            [styles['lead-info--two-rows']]: hideRelatedEntity,
            [styles['lead-info--two-rows-small']]: !hideRelatedEntity && this.props.isSmall,
        });

        const topRowStyleName = classnames(styles['top-row'], {
            [styles['top-row--larger']]: this.props.largerLeadName,
            [styles['top-row--no-center-align']]: this.props.noCenterAlignTopRow,
        });

        return (
            <div className={styleNames}>
                <LeadAvatar avatarUrl={this.props.avatarUrl} leadNumber={this.props.leadNumber} />
                <div className={leadInfoStyleName}>
                    <div className={topRowStyleName}>
                        <LinkedLeadEntity
                            isLinkable={this.props.isLinkable}
                            newTab={this.props.newTab}
                            lead={{
                                name: sanitizedLeadName,
                                priority: this.props.priority,
                                status: this.props.leadStatus,
                                htmlUrl: this.props.htmlUrl,
                            }}
                            showFlame={true}
                            disableRouting={this.props.disableRouting}
                        />
                        {this.props.formattedValue ? (
                            <LeadFormattedValue formattedValue={this.props.formattedValue} />
                        ) : undefined}
                        {this.props.rightContent}
                    </div>
                    {bottomRowContent ? (
                        <div className={styles['bottom-row']}>{bottomRowContent}</div>
                    ) : undefined}
                </div>
            </div>
        );
    }

    renderLoadingPlaceholder = () => {
        // This is really hacky, but we are currently always show a lead number on the lead list page,
        // so if that's where this component is being rendered, let's leave a placeholder space for the number.
        const goingToHaveNumber = Boolean(
            this.props.leadNumber || window.location.pathname === '/leads'
        );

        return (
            <div
                className={
                    goingToHaveNumber
                        ? styles['lead-two-line--with-number']
                        : styles['lead-two-line']
                }
            >
                <LeadAvatar isLoading={true} includeNumber={goingToHaveNumber} />
                <div className={styles['lead-info']}>
                    <div className={styles['top-row']}>
                        <div style={{height: 17, paddingTop: 4}}>
                            <LoadingText height={13} color={colors.offWhiteDk} />
                        </div>
                    </div>
                    <div className={styles['bottom-row']}>
                        <LoadingText height={13} color={colors.offWhiteMd} />
                    </div>
                </div>
            </div>
        );
    };
}
